import { useMemo, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { useMutation } from "@tanstack/react-query";
import BrandLogo from "../../assets/sidebar-nav/brand-logo.svg";
import BaseButton from "../../components/ui/BaseButton";
import BaseTextField from "../../components/ui/BaseTextField";
import { companySignup } from "../../services/api";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { showToast } from "../../store/toast/toastSlice";
import BaseWrapper from "../../components/ui/BaseWrapper";
import BaseInnerWrapper from "../../components/ui/BaseInnerWrapper";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import PasswordChecker from "../../components/ui/PasswordChecker";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { FormControl, FormHelperText, InputLabel, MenuItem, Select } from "@mui/material";
import TechnicianPrivacyPolicy from "./TechnicianPrivacyPolicy";
import TechnicianTermsConditions from "./TechnicianTermsAndConditions";
import useSignupTicket from "../../hooks/useSignupTicket";
import { setLoginSuccessful } from "../../store/authUser/authUserSlice";
/** Signing up through Google means there is no password to collect — the provider
 *  is the credential. Everything else is validated exactly as before. */
const buildValidationSchema = (isSocial) =>
  yup.object({
    firstName: yup
      .string("Enter your first name")
      .matches(/^[A-Za-z\s]+$/, "First name must only contain alphabets")
      .required("First name is required"),
    lastName: yup
      .string("Enter your last name")
      .matches(/^[A-Za-z\s]+$/, "Last name must only contain alphabets")
      .required("Last name is required"),
    phoneNumber: yup.string("Enter your phone number").required("Phone number is required"),
    referralSource: yup.string("Enter your source").required("Source is required"),
    email: yup
      .string("Enter your email")
      .email("Enter a valid email")
      .required("Email is required"),
    password: isSocial
      ? yup.string().notRequired()
      : yup
          .string()
          .required("Password is required")
          .min(8, "Password should be at least 8 characters long")
          .max(20, "Password should be at most 20 characters long")
          .test("has-uppercase", "Password must contain at least one uppercase letter", (value) =>
            /[A-Z]/.test(value),
          )
          .test("has-lowercase", "Password must contain at least one lowercase letter", (value) =>
            /[a-z]/.test(value),
          )
          .test("has-number", "Password must contain at least one number", (value) =>
            /[0-9]/.test(value),
          )
          .test("has-symbol", "Password must contain at least one symbol", (value) =>
            /[!@#$%^&*]/.test(value),
          ),

    // address: yup.string("Enter your address").required("Address is required"),
    isAgree: yup.boolean().oneOf([true], "You must agree to proceed").default(false),
  });

const TechnicianSignupForm = () => {
  const [checked, setChecked] = useState(false);
  const [isIndividual, setIsIndividual] = useState(null);
  const [companyName, setCompanyName] = useState("");
  const [companyNameError, setCompanyNameError] = useState("");
  const [selectedLocation, setSelectedLocation] = useState({});
  const [isPrivacyStatementOpen, setPrivacyStatementOpen] = useState(false);
  const [isTermsAndConditionsOpen, setTermsAndConditionsOpen] = useState(false);
  const [isShowPassword, setIsShowPassword] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Present when the user got here by signing up with Google.
  const { ticket, prefill, isExpired, isSocial } = useSignupTicket();

  const hasProviderType = isIndividual !== null;

  const {
    isPending: isSignupLoading,
    status,
    error,
    mutateAsync: signupMutation,
  } = useMutation({
    mutationFn: companySignup,
    onSuccess: (data) => {
      // Google already verified the address, so there is no OTP step — the backend
      // hands back tokens and we log them straight in.
      if (isSocial) {
        dispatch(
          setLoginSuccessful({
            user: data.data,
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
          }),
        );
        return;
      }
      otpSentSuccessfully();
    },
    onError: (error) => {},
  });
  const handleChangeChecked = (event) => {
    setChecked(event.target.checked);
  };
  const formik = useFormik({
    // Prefill arrives a moment after mount, once the ticket has been read.
    enableReinitialize: true,
    initialValues: {
      firstName: prefill?.firstName ?? "",
      lastName: prefill?.lastName ?? "",
      // name: "",
      phoneNumber: "",
      email: prefill?.email ?? "",
      password: "",
      referralSource: "",
      // address: "",
      isAgree: true,
    },
    validationSchema: useMemo(() => buildValidationSchema(isSocial), [isSocial]),
    onSubmit: async (values) => {
      handleTechSignup();
    },
    validateOnChange: false,
    validateOnBlur: false,
  });

  const handleTechSignup = async () => {
    if (isIndividual === false && !companyName.trim()) {
      setCompanyNameError("Company name is required");
      return;
    }
    setCompanyNameError("");
    try {
      const payload = {
        ...formik.values,
        address: selectedLocation,
        phoneNumber: formik.values.phoneNumber.toString(),
        ...(isIndividual === false && companyName.trim()
          ? { freelanceCompanyName: companyName.trim() }
          : {}),
        // The backend takes the email from the ticket rather than this payload, so a
        // tampered email field can't be used to register as somebody else.
        ...(isSocial ? { ticket, password: undefined } : {}),
      };

      await signupMutation(payload);
    } catch (error) {
      console.log(error);
      if (error?.response?.status === 403) {
        const email = encodeURIComponent(formik.values.email);

        navigate(`/otp?email=${email}`);
      }
      dispatch(
        showToast({
          message: error?.response?.data?.message,
          type: "error",
        }),
      );
    }
  };

  const otpSentSuccessfully = () => {
    const email = encodeURIComponent(formik.values.email);

    navigate(`/otp?email=${email}`);
  };

  return (
    <BaseWrapper>
      <BaseInnerWrapper
        className={`min-h-screen flex flex-col ${
          hasProviderType ? "" : "justify-center"
        } lg:py-[clamp(16px,3vh,64px)] py-8  2xl:mx-64 xl:mx-24 lg:mx-24 mx-0 xl:px-[70px] px-[0px]`}
      >
        {/* Stretches to the screen only once the form is showing; before that it
            wraps the short question block instead of leaving a tall empty box. */}
        <div
          className={`${
            hasProviderType ? "flex-1" : ""
          } lg:border border-zinc-300 rounded-lg lg:pt-[clamp(32px,5vh,96px)] lg:pb-10 lg:px-24  flex flex-col items-center`}
        >
          <div className=" flex flex-col justify-center items-center cursor-default">
            <img src={BrandLogo} width={110} />
            {/* Coming from Google, the account is already half made — "Let's create"
                would read as if the last step hadn't counted. */}
            <h1 className="text-4xl text-[#525354] text-center mt-6 mb-2 lg:leading-[70px]">
              {isSocial ? "Complete your account setup" : "Let’s Create Your Account"}
            </h1>
            <p
              className={`text-base text-[#525354] text-center font-poppins px-8 text-[16px] ${
                isSocial ? "mb-10" : "mb-14"
              }`}
            >
              {isSocial ? (
                "Just a few details left and you’re done"
              ) : (
                <>
                  <em>Connect. Work. Grow.</em>
                  <br />
                  Create your professional profile and access field service opportunities that match
                  your expertise and availability.
                </>
              )}
            </p>

            {isExpired && (
              <div className="w-full border border-red-300 bg-red-50 rounded-lg px-8 py-6 mb-12">
                <p className="text-dark text-base text-center">
                  Your Google sign-in link has expired. Please{" "}
                  <Link className="text-primary underline underline-offset-4" to="/getStarted">
                    start again
                  </Link>
                  .
                </p>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-6  w-full">
            <div className="col-span-2">
              <p
                className="text-sm  text-[#525354] font-medium font-poppins text-[16px] mb-4"
                style={{ marginLeft: 4 }}
              >
                How do you provide services? <span className="text-red-500">*</span>
              </p>
              {/* Both cards always carry a 2px border so switching never changes their size. */}
              <div className="grid grid-cols-2 gap-3">
                <div className="md:col-span-1 col-span-2">
                  <div
                    className={`flex items-center h-full px-4 py-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      isIndividual === true
                        ? "border-primary bg-[#EEF3FC]"
                        : "border-zinc-200 bg-white"
                    }`}
                    onClick={() => {
                      setIsIndividual(true);
                      setCompanyName("");
                      setCompanyNameError("");
                    }}
                  >
                    {isIndividual === true ? (
                      <div className="rounded-full border-2 border-primary flex items-center justify-center min-h-[18px] min-w-[18px]">
                        <div className="rounded-full bg-primary min-h-[8px] min-w-[8px]"></div>
                      </div>
                    ) : (
                      <div className="rounded-full border-2 border-[#D8EBF8] min-h-[18px] min-w-[18px]"></div>
                    )}
                    <div className="ml-3">
                      <p className="text-sm font-normal text-[#525354] font-poppins">
                        Independent Technician
                      </p>
                      <p className="text-xs italic text-[#525354] font-poppins mt-1">
                        I provide field services independently.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="md:col-span-1 col-span-2">
                  <div
                    className={`flex items-center h-full px-4 py-4 border-2 rounded-lg cursor-pointer transition-colors ${
                      isIndividual === false
                        ? "border-primary bg-[#EEF3FC]"
                        : "border-zinc-200 bg-white"
                    }`}
                    onClick={() => {
                      setIsIndividual(false);
                      setCompanyName("");
                      setCompanyNameError("");
                    }}
                  >
                    {isIndividual === false ? (
                      <div className="rounded-full border-2 border-primary flex items-center justify-center min-h-[18px] min-w-[18px]">
                        <div className="rounded-full bg-primary min-h-[8px] min-w-[8px]"></div>
                      </div>
                    ) : (
                      <div className="rounded-full border-2 border-[#D8EBF8] min-h-[18px] min-w-[18px]"></div>
                    )}
                    <div className="ml-3">
                      <p className="text-sm font-normal text-[#525354] font-poppins">
                        Company or Team
                      </p>
                      <p className="text-xs italic text-[#525354] font-poppins mt-1">
                        I manage or work with multiple technicians.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              {isIndividual === false && (
                <div className="mt-6">
                  <BaseTextField
                    height="60px"
                    fullWidth
                    label={
                      <span className="text-[#525354]">
                        Your company name <span className="text-red-500">*</span>
                      </span>
                    }
                    InputLabelProps={{ shrink: true }}
                    placeholder="Company name"
                    value={companyName}
                    onChange={(e) => {
                      setCompanyName(e.target.value);
                      if (e.target.value.trim()) setCompanyNameError("");
                    }}
                    error={Boolean(companyNameError)}
                    helperText={companyNameError}
                  />
                  <p className="text-xs text-[#525354] mt-2">
                    Note: The type of account selected will require you to provide an associated{" "}
                    <strong> Identification</strong>. If you are an individual provider looking for
                    work, please select Independent Technician in the question above.
                  </p>
                </div>
              )}
            </div>

            {hasProviderType && (
              <>
                <div className="lg:col-span-1 col-span-2">
                  <BaseTextField
                    height="60px"
                    label={
                      <span className="text-[#525354]">
                        First Name <span className="text-red-500">*</span>
                      </span>
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                    labelShrink={true}
                    fullWidth
                    id="firstName"
                    name="firstName"
                    placeholder="William"
                    // Locked only when Google actually gave us a name — a disabled empty
                    // field would leave a required value with no way to fill it.
                    disabled={isSocial && Boolean(prefill?.firstName)}
                    value={formik.values.firstName}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^[A-Za-z\s]*$/.test(value)) {
                        formik.handleChange(e);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                    helperText={formik.touched.firstName && formik.errors.firstName}
                  />
                </div>
                <div className="lg:col-span-1 col-span-2">
                  <BaseTextField
                    height="60px"
                    fullWidth
                    id="lastName"
                    name="lastName"
                    placeholder="Clark"
                    disabled={isSocial && Boolean(prefill?.lastName)}
                    label={
                      <span className="text-[#525354] ">
                        Last Name <span className="text-red-500">*</span>
                      </span>
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                    value={formik.values.lastName}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^[A-Za-z\s]*$/.test(value)) {
                        formik.handleChange(e);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                    helperText={formik.touched.lastName && formik.errors.lastName}
                  />
                </div>
                <div className="lg:col-span-1 col-span-2">
                  <div>
                    <PhoneInput
                      inputClass={`
        !w-full  !font-normal
          !text-[#686868] !bg-white !border !rounded-lg
        ${
          formik.touched.phoneNumber && formik.errors.phoneNumber
            ? "!border-red-600"
            : "!border-gray-300 focus:!border-[#0F40AE]"
        }
        focus:!outline-none focus:!ring-1 focus:!ring-[#0F40AE]
      `}
                      buttonClass="!border-none !bg-transparent"
                      specialLabel={
                        <span className="!text-[#525354] text-xs">
                          Phone Number <span className="text-red-500">*</span>
                        </span>
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputStyle={{
                        width: "100%",
                        borderRadius: "8px",
                        fontSize: "14px",
                        padding: "14px 14px 11px 58px",
                        color: "#686868",
                        ...(formik.touched.phoneNumber &&
                          formik.errors.phoneNumber && {
                            border: "1px solid #d32f2f",
                          }),
                      }}
                      className="w-full"
                      country={"au"}
                      id="phoneNumber"
                      name="phoneNumber"
                      value={formik.values.phoneNumber}
                      onChange={(value) => formik.setFieldValue("phoneNumber", value)} // Update formik field value
                      onBlur={() => formik.setFieldTouched("phoneNumber", true)} // Manually trigger touch
                      type="number"
                    />
                    {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                      <FormHelperText error>{formik.errors.phoneNumber}</FormHelperText>
                    )}
                  </div>
                </div>

                <div className="lg:col-span-1 col-span-2">
                  <BaseTextField
                    height="60px"
                    fullWidth
                    id="email"
                    name="email"
                    label={
                      <span className="text-[#525354]">
                        Email <span className="text-red-500">*</span>
                      </span>
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                    placeholder="Email"
                    disabled={isSocial}
                    value={(isSocial && prefill?.emailMasked) || formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                  />
                </div>
                {/* No password when signing up with Google — the provider is the
                credential. They can still set one later via Forgot Password. */}
                {!isSocial && (
                  <div className="col-span-2">
                    <BaseTextField
                      endAdornment={
                        <i
                          className={`fa-regular ${
                            isShowPassword ? "fa-eye" : "fa-eye-slash"
                          } text-base cursor-pointer`}
                          onClick={() => setIsShowPassword(!isShowPassword)}
                        ></i>
                      }
                      height="60px"
                      fullWidth
                      id="password"
                      name="password"
                      placeholder="Hello@321"
                      label={
                        <span className="text-[#525354]">
                          Password <span className="text-red-500">*</span>
                        </span>
                      }
                      InputLabelProps={{
                        shrink: true,
                      }}
                      type={isShowPassword ? "text" : "password"}
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.password && Boolean(formik.errors.password)}
                      helperText={formik.touched.password && formik.errors.password}
                      sx={{
                        // Hide Edge's built-in reveal/clear buttons
                        "& input::-ms-reveal": { display: "none" },
                        "& input::-ms-clear": { display: "none" },
                      }}
                    />
                  </div>
                )}
                {/* <div className="lg:col-span-1 col-span-2">
              <SearchLocation
                height="60px"
                name="address"
                labelShrink={true}
                isDefaultField={true}
                label="Address"
                onChange={formik.handleChange}
                onPlaceSelected={handleLocationSearch}
                onBlur={formik.handleBlur}
                error={formik.touched.address && Boolean(formik.errors.address)}
                // helperText={formik.touched.address && formik.errors.address}
              />
            </div> */}
                {!isSocial && formik.values.password && (
                  <div className="col-span-2">
                    <PasswordChecker password={formik.values.password} columns={2} />
                  </div>
                )}

                <div
                  className={`col-span-2 ${
                    formik.touched.referralSource && formik.errors.referralSource
                      ? "h-[80px]"
                      : "h-[60px]"
                  }`}
                >
                  <FormControl
                    fullWidth
                    required
                    error={formik.touched.referralSource && Boolean(formik.errors.referralSource)}
                    variant="outlined"
                    style={{ height: "100%" }} // Optional: Adjust the margin as needed
                  >
                    <InputLabel
                      id="referralSource"
                      required
                      shrink
                      sx={{
                        "&.MuiInputLabel-shrink": {
                          transform: "translate(14px, -9px) scale(0.75)",
                        },
                        "& .MuiFormLabel-asterisk": {
                          color: "red",
                          fontSize: "16px",
                        },
                      }}
                    >
                      Where did you hear about us?
                    </InputLabel>

                    <Select
                      className="rounded-lg"
                      labelId="referralSource"
                      id="referralSource"
                      name="referralSource"
                      value={formik.values.referralSource}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      label="Where did you hear about us?"
                      displayEmpty // ✅ ensures the label is floating even when value is empty
                      sx={{ fontSize: "0.875rem", "& .MuiSelect-select": { padding: "14px" } }}
                    >
                      <MenuItem value="LINKEDIN" className="text-[#525354] text-sm">
                        LinkedIn
                      </MenuItem>
                      <MenuItem value="INTERNET_SEARCH" className="text-[#525354] text-sm">
                        Internet Search
                      </MenuItem>
                      <MenuItem value="WORD_OF_MOUTH" className="text-[#525354] text-sm">
                        Word of mouth
                      </MenuItem>
                    </Select>
                    {formik.touched.referralSource && formik.errors.referralSource && (
                      <FormHelperText>{formik.errors.referralSource}</FormHelperText>
                    )}
                  </FormControl>
                </div>
                <div className="flex items-center mt-3 col-span-2 justify-start ml-2">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={handleChangeChecked}
                        color="primary"
                        style={{ padding: 0, margin: 0 }}
                      />
                    }
                    label=""
                    style={{ marginRight: 0 }}
                  />
                  <p
                    className="text-[#525354] font-normal text-sm font-poppins mb-0"
                    style={{ marginLeft: 4 }}
                  >
                    I confirm that I agree to the{" "}
                    <span
                      className="text-primary text-sm cursor-pointer"
                      onClick={() => setPrivacyStatementOpen(true)}
                    >
                      Privacy Statement{" "}
                    </span>
                    <span className="text-sm">& </span>
                    <span
                      className="text-primary text-sm cursor-pointer"
                      onClick={() => setTermsAndConditionsOpen(true)}
                    >
                      Terms of Services
                    </span>
                  </p>
                </div>
              </>
            )}
          </div>

          {hasProviderType && (
            <BaseButton
              isLoading={isSignupLoading}
              onClick={formik.handleSubmit}
              className="mt-8 rounded-full px-[80px] py-[8px] text-base"
              color="primary"
              variant="contained"
              type="submit"
              disabled={
                !checked ||
                !formik.values.firstName ||
                !formik.values.lastName ||
                !formik.values.phoneNumber ||
                !formik.values.email ||
                (!isSocial && !formik.values.password) ||
                !formik.values.referralSource ||
                (isIndividual === false && !companyName.trim())
              }
            >
              {isSocial ? "Complete Setup" : "Create Account"}
            </BaseButton>
          )}

          <p className={`text-dark text-base ${hasProviderType ? "mt-8" : "mt-12"}`}>
            Already have an account?{" "}
            <Link
              className="text-base underline underline-offset-[6px] text-primary font-normal"
              to="/login"
            >
              Login
            </Link>
          </p>
          <p className={`text-base font-medium text-[#525354] ${hasProviderType ? "mt-6" : "mt-8"}`}>
            Copyright @2025 Tech Domain. All rights reserved.
          </p>
          {isPrivacyStatementOpen && (
            <TechnicianPrivacyPolicy
              open={isPrivacyStatementOpen}
              close={() => setPrivacyStatementOpen(false)}
            />
          )}
          {isTermsAndConditionsOpen && (
            <TechnicianTermsConditions
              open={isTermsAndConditionsOpen}
              close={() => setTermsAndConditionsOpen(false)}
            />
          )}
        </div>
      </BaseInnerWrapper>
    </BaseWrapper>
  );
};

export default TechnicianSignupForm;
